import React, { useState, useEffect } from "react";
import CustomerLayout from "./CustomerLayout";
import { API_URL } from "../config";
import {
    Car,
    Calendar,
    DollarSign,
    Clock,
    Search,
    Download,
    Eye,
    Edit3,
    Trash2,
    MapPin,
    ShieldCheck,
    Ban,
    Loader2,
    X,
    AlertTriangle,
    RefreshCw,
} from "lucide-react";

interface CarRental {
    id: number;
    car_id: number;
    car: {
        id: number;
        name: string;
        model: string;
        type: string;
        photos: Array<{ photo_path: string }>;
    };
    email: string;
    firstname: string;
    lastname: string;
    phone: string;
    address: string;
    new_price: number;
    duration: number;
    rate_type: string;
    booking_date: string;
    end_date: string;
    document_type: string | null;
    insurance_document: string | null;
    notes: string | null;
    status: "pending" | "confirmed" | "cancelled" | "completed";
    created_at: string;
    updated_at: string;
}

interface FormattedRental {
    id: number;
    carModel: string;
    carType: string;
    bookingReference: string;
    pickupLocation: string;
    pickupDate: string;
    returnDate: string;
    bookingDate: string;
    amount: number;
    status: "Confirmed" | "Pending" | "Completed" | "Cancelled";
    transmission: string;
    carImage?: string;
}

const MyRentCars: React.FC = () => {
    const [rentals, setRentals] = useState<FormattedRental[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [activeFilter, setActiveFilter] = useState("All Rentals");

    // Modal states
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
    const [rentalToDelete, setRentalToDelete] = useState<FormattedRental | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false);
    const [rentalToCancel, setRentalToCancel] = useState<FormattedRental | null>(null);
    const [isCancelling, setIsCancelling] = useState<boolean>(false);
    const [cancelError, setCancelError] = useState<string | null>(null);

    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState<boolean>(false);
    const [selectedRental, setSelectedRental] = useState<FormattedRental | null>(null);

    const formatDate = (dateString?: string) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric', 
            year: 'numeric' 
        });
    };

    const mapStatus = (status: string): FormattedRental["status"] => {
        const map: Record<string, FormattedRental["status"]> = {
            'pending': 'Pending',
            'confirmed': 'Confirmed',
            'cancelled': 'Cancelled',
            'completed': 'Completed'
        };
        return map[status] || 'Pending';
    };

    const getStatusColor = (status: string) => {
        switch (status.toLowerCase()) {
            case 'confirmed': return { bg: '#e6f4ea', text: '#1e7e34', dot: '#1e7e34' };
            case 'pending': return { bg: '#fef3d6', text: '#b25900', dot: '#b25900' };
            case 'completed': return { bg: '#e8f0fe', text: '#1a73e8', dot: '#1a73e8' };
            case 'cancelled': return { bg: '#fce8e6', text: '#c5221f', dot: '#c5221f' };
            default: return { bg: '#f0f0f0', text: '#666', dot: '#666' };
        }
    };

    const getCarType = (type: string): string => {
        const types: Record<string, string> = {
            'suv': 'SUV',
            'sedan': 'Sedan',
            'coupe': 'Coupe',
            'convertible': 'Cabriolet',
            'hatchback': 'Hatchback',
            'wagon': 'Wagon',
            'van': 'Van',
            'truck': 'Truck',
            'luxury': 'Luxury',
            'sports': 'Sports'
        };
        return types[type?.toLowerCase()] || type || 'Car';
    };

    // Fetch rentals
    useEffect(() => {
        const fetchRentals = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await fetch(`${API_URL}/customer/my-car-bookings`, {
                    headers: {
                        "Content-Type": "application/json",
                        "Accept": "application/json",
                        Authorization: `Bearer ${localStorage.getItem("token_customer_silverjarz") || ""}`,
                    },
                });

                if (!response.ok) {
                    throw new Error("Failed to fetch your car bookings.");
                }

                const payload = await response.json();
                const rawRentals = payload.data || [];

                const formatted: FormattedRental[] = rawRentals.map((item: CarRental) => ({
                    id: item.id,
                    carModel: item.car?.name || "Unknown Vehicle",
                    carType: getCarType(item.car?.type || ""),
                    bookingReference: `CAR-${String(item.id).padStart(6, '0')}`,
                    pickupLocation: item.address || "N/A",
                    pickupDate: item.booking_date || "",
                    returnDate: item.end_date || "",
                    bookingDate: item.created_at || "",
                    amount: item.new_price || 0,
                    status: mapStatus(item.status),
                    transmission: "Automatic",
                    carImage: item.car?.photos?.[0]?.photo_path || undefined,
                }));

                setRentals(formatted);
            } catch (err: any) {
                console.error("Fetch Rentals Error:", err);
                setError(err.message || "Unable to load car bookings.");
                setRentals([]);
            } finally {
                setLoading(false);
            }
        };

        fetchRentals();
    }, []);

    // Delete handlers
    const openDeleteModal = (rental: FormattedRental) => {
        setRentalToDelete(rental);
        setDeleteError(null);
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!rentalToDelete) return;

        setIsDeleting(true);
        setDeleteError(null);

        try {
            const response = await fetch(`${API_URL}/customer/my-car-bookings/${rentalToDelete.id}`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token_customer_silverjarz") || ""}`,
                },
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || "Failed to delete booking.");
            }

            setRentals((prev) => prev.filter((r) => r.id !== rentalToDelete.id));
            setIsDeleteModalOpen(false);
            setRentalToDelete(null);
        } catch (err: any) {
            setDeleteError(err.message || "Deletion error.");
        } finally {
            setIsDeleting(false);
        }
    };

    // Cancel handlers
    const openCancelModal = (rental: FormattedRental) => {
        setRentalToCancel(rental);
        setCancelError(null);
        setIsCancelModalOpen(true);
    };

    const confirmCancel = async () => {
        if (!rentalToCancel) return;

        setIsCancelling(true);
        setCancelError(null);

        try {
            const response = await fetch(`${API_URL}/customer/my-car-bookings/${rentalToCancel.id}/cancel`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token_customer_silverjarz") || ""}`,
                },
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || "Failed to cancel booking.");
            }

            setRentals((prev) =>
                prev.map((r) => (r.id === rentalToCancel.id ? { ...r, status: "Cancelled" } : r))
            );
            setIsCancelModalOpen(false);
            setRentalToCancel(null);
        } catch (err: any) {
            setCancelError(err.message || "Cancellation error.");
        } finally {
            setIsCancelling(false);
        }
    };

    // View details
    const handleViewDetails = (rental: FormattedRental) => {
        setSelectedRental(rental);
        setIsDetailsModalOpen(true);
    };

    // Stats
    const totalRentals = rentals.length;
    const activeRentals = rentals.filter(
        (r) => r.status === "Confirmed" && new Date(r.pickupDate) >= new Date()
    ).length;
    const totalSpent = rentals
        .filter((r) => r.status !== "Cancelled")
        .reduce((sum, r) => sum + r.amount, 0);

    const filteredRentals = rentals.filter((rental) => {
        const matchesSearch =
            rental.carModel.toLowerCase().includes(searchTerm.toLowerCase()) ||
            rental.bookingReference.toLowerCase().includes(searchTerm.toLowerCase()) ||
            rental.pickupLocation.toLowerCase().includes(searchTerm.toLowerCase());

        if (!matchesSearch) return false;

        if (activeFilter === "Confirmed") return rental.status === "Confirmed";
        if (activeFilter === "Pending") return rental.status === "Pending";
        if (activeFilter === "Completed") return rental.status === "Completed";
        if (activeFilter === "Cancelled") return rental.status === "Cancelled";

        return true;
    });

    return (
        <CustomerLayout>
            <style>{`
                .my-cars-container {
                    max-width: 1400px;
                    margin: 0 auto;
                    padding: 0 24px 40px;
                }

                .page-header-modern {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 32px;
                    flex-wrap: wrap;
                    gap: 16px;
                }

                .page-header-modern h1 {
                    font-size: 28px;
                    font-weight: 700;
                    color: #1a1a2e;
                    margin: 0 0 4px 0;
                }

                .page-header-modern p {
                    font-size: 15px;
                    color: #6b7280;
                    margin: 0;
                }

                .header-actions {
                    display: flex;
                    gap: 12px;
                    align-items: center;
                }

                .btn-refresh {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 20px;
                    background: #ffffff;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    font-size: 13px;
                    font-weight: 600;
                    color: #374151;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .btn-refresh:hover {
                    background: #f9fafb;
                    border-color: #d1d5db;
                }

                .cars-stats-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 16px;
                    margin-bottom: 32px;
                }

                .car-stat-card {
                    background: #ffffff;
                    border-radius: 16px;
                    padding: 20px 24px;
                    border: 1px solid #f0f0f0;
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    transition: all 0.3s ease;
                }

                .car-stat-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 24px rgba(0,0,0,0.06);
                }

                .car-stat-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 14px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }

                .car-stat-content h4 {
                    font-size: 24px;
                    font-weight: 700;
                    color: #1a1a2e;
                    margin: 0 0 2px 0;
                    line-height: 1.2;
                }

                .car-stat-content p {
                    font-size: 13px;
                    color: #6b7280;
                    margin: 0;
                }

                .filters-modern {
                    background: #ffffff;
                    border-radius: 16px;
                    padding: 16px 20px;
                    border: 1px solid #f0f0f0;
                    display: flex;
                    flex-wrap: wrap;
                    gap: 16px;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 24px;
                }

                .search-modern {
                    position: relative;
                    flex: 1;
                    min-width: 240px;
                }

                .search-modern input {
                    width: 100%;
                    padding: 10px 14px 10px 40px;
                    border-radius: 10px;
                    border: 1px solid #e5e7eb;
                    background: #f9fafb;
                    font-size: 14px;
                    outline: none;
                    color: #1a1a2e;
                    transition: all 0.2s ease;
                    box-sizing: border-box;
                }

                .search-modern input:focus {
                    border-color: #294b5a;
                    background: #ffffff;
                    box-shadow: 0 0 0 4px rgba(41, 75, 90, 0.08);
                }

                .search-modern svg {
                    position: absolute;
                    left: 13px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #9ca3af;
                }

                .filter-group {
                    display: flex;
                    gap: 6px;
                    flex-wrap: wrap;
                }

                .filter-btn-modern {
                    padding: 8px 18px;
                    border: 1px solid #e5e7eb;
                    border-radius: 20px;
                    background: #ffffff;
                    font-size: 13px;
                    font-weight: 500;
                    color: #6b7280;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .filter-btn-modern:hover {
                    background: #f9fafb;
                    border-color: #d1d5db;
                }

                .filter-btn-modern.active {
                    background: #294b5a;
                    color: #ffffff;
                    border-color: #294b5a;
                }

                .cars-grid {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 16px;
                }

                .car-card {
                    background: #ffffff;
                    border-radius: 16px;
                    padding: 20px 24px;
                    border: 1px solid #f0f0f0;
                    display: grid;
                    grid-template-columns: 1fr 1fr 1fr auto;
                    align-items: center;
                    gap: 16px;
                    transition: all 0.3s ease;
                }

                .car-card:hover {
                    box-shadow: 0 4px 20px rgba(0,0,0,0.06);
                    border-color: #e5e7eb;
                }

                .car-card-left {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                }

                .car-icon-box {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    background: #f3f4f6;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }

                .car-info h4 {
                    font-size: 15px;
                    font-weight: 600;
                    color: #1a1a2e;
                    margin: 0 0 3px 0;
                }

                .car-info .car-ref {
                    font-size: 12px;
                    color: #6b7280;
                    font-family: monospace;
                }

                .car-route-modern {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .route-point-car {
                    text-align: center;
                }

                .route-point-car .city {
                    font-size: 14px;
                    font-weight: 600;
                    color: #1a1a2e;
                }

                .route-point-car .code {
                    font-size: 11px;
                    color: #6b7280;
                    font-weight: 500;
                }

                .route-line-car {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    min-width: 30px;
                }

                .route-line-car .line {
                    width: 24px;
                    height: 2px;
                    background: #e5e7eb;
                    position: relative;
                }

                .route-line-car .line::after {
                    content: '';
                    position: absolute;
                    right: -4px;
                    top: -3px;
                    border-left: 6px solid #e5e7eb;
                    border-top: 4px solid transparent;
                    border-bottom: 4px solid transparent;
                }

                .car-meta {
                    display: flex;
                    gap: 16px;
                    align-items: center;
                }

                .meta-item-car {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                }

                .meta-item-car .label {
                    font-size: 10px;
                    text-transform: uppercase;
                    color: #9ca3af;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                }

                .meta-item-car .value {
                    font-size: 13px;
                    font-weight: 600;
                    color: #1a1a2e;
                    margin-top: 2px;
                }

                .car-status-modern {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 4px 12px 4px 8px;
                    border-radius: 20px;
                    font-size: 12px;
                    font-weight: 600;
                }

                .status-dot {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                }

                .car-actions {
                    display: flex;
                    gap: 4px;
                }

                .action-btn-modern {
                    width: 36px;
                    height: 36px;
                    border-radius: 10px;
                    border: none;
                    background: transparent;
                    color: #6b7280;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .action-btn-modern:hover {
                    background: #f3f4f6;
                    color: #1a1a2e;
                }

                .action-btn-modern.delete:hover {
                    background: #fef2f2;
                    color: #dc2626;
                }

                .empty-state-modern {
                    text-align: center;
                    padding: 60px 20px;
                    background: #ffffff;
                    border-radius: 16px;
                    border: 1px solid #f0f0f0;
                }

                .loading-state-modern {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 60px 20px;
                    background: #ffffff;
                    border-radius: 16px;
                    border: 1px solid #f0f0f0;
                    gap: 16px;
                }

                .spinner-modern {
                    animation: spin 0.8s linear infinite;
                    color: #294b5a;
                }

                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }

                .error-box-modern {
                    padding: 14px 18px;
                    background: #fef2f2;
                    color: #dc2626;
                    border-radius: 12px;
                    font-size: 14px;
                    font-weight: 500;
                    border: 1px solid #fecaca;
                    margin-bottom: 20px;
                }

                /* Modal Styles */
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100vw;
                    height: 100vh;
                    background: rgba(0, 0, 0, 0.5);
                    backdrop-filter: blur(8px);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    padding: 20px;
                    animation: fadeIn 0.2s ease;
                }

                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                @keyframes slideUp {
                    from { 
                        opacity: 0;
                        transform: translateY(20px) scale(0.95);
                    }
                    to { 
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                }

                .modal-content {
                    background: #ffffff;
                    border-radius: 20px;
                    max-width: 650px;
                    width: 100%;
                    max-height: 90vh;
                    overflow-y: auto;
                    box-shadow: 0 25px 50px rgba(0,0,0,0.2);
                    padding: 32px;
                    position: relative;
                    animation: slideUp 0.25s ease;
                }

                .modal-content.delete-modal {
                    max-width: 440px;
                    text-align: center;
                }

                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding-bottom: 16px;
                    border-bottom: 1px solid #edf2f4;
                    margin-bottom: 4px;
                }

                .modal-header h3 {
                    margin: 0;
                    font-size: 20px;
                    color: #1a1a2e;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    font-weight: 700;
                }

                .modal-close-btn {
                    background: none;
                    border: none;
                    color: #6b7280;
                    cursor: pointer;
                    padding: 6px;
                    border-radius: 50%;
                    transition: all 0.2s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .modal-close-btn:hover {
                    background: #f3f4f6;
                    color: #1a1a2e;
                }

                .modal-body {
                    margin-top: 20px;
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                }

                .modal-section {
                    background: #f8fafc;
                    padding: 18px 20px;
                    border-radius: 12px;
                    border: 1px solid #eef2f6;
                }

                .modal-section-title {
                    font-size: 12px;
                    font-weight: 700;
                    color: #6b7280;
                    text-transform: uppercase;
                    letter-spacing: 0.8px;
                    margin-bottom: 14px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .details-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 12px 20px;
                }

                .detail-item {
                    display: flex;
                    flex-direction: column;
                    gap: 2px;
                }

                .detail-label {
                    font-size: 11px;
                    color: #9ca3af;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .detail-value {
                    font-size: 14px;
                    font-weight: 600;
                    color: #1a1a2e;
                }

                .delete-icon-wrapper {
                    width: 60px;
                    height: 60px;
                    border-radius: 50%;
                    background: #fef2f2;
                    color: #dc2626;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 16px auto;
                }

                .delete-modal-title {
                    font-size: 20px;
                    font-weight: 700;
                    color: #1a1a2e;
                    margin: 0 0 8px 0;
                }

                .delete-modal-desc {
                    font-size: 14px;
                    color: #6b7280;
                    margin: 0 0 20px 0;
                    line-height: 1.6;
                }

                .delete-flight-summary {
                    background: #f8fafc;
                    padding: 12px 16px;
                    border-radius: 12px;
                    margin-bottom: 24px;
                    font-size: 14px;
                    font-weight: 600;
                    color: #1a1a2e;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    border: 1px solid #eef2f6;
                }

                .delete-actions {
                    display: flex;
                    gap: 12px;
                }

                .btn-cancel {
                    flex: 1;
                    padding: 12px 16px;
                    border: 1px solid #e5e7eb;
                    background: #ffffff;
                    color: #374151;
                    border-radius: 10px;
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .btn-cancel:hover {
                    background: #f9fafb;
                    border-color: #d1d5db;
                }

                .btn-cancel:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .btn-delete-confirm {
                    flex: 1;
                    padding: 12px 16px;
                    border: none;
                    background: #dc2626;
                    color: #ffffff;
                    border-radius: 10px;
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    transition: all 0.2s ease;
                }

                .btn-delete-confirm:hover {
                    background: #b91c1c;
                }

                .btn-delete-confirm:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .error-box {
                    padding: 12px 16px;
                    background: #fef2f2;
                    color: #dc2626;
                    border-radius: 8px;
                    font-size: 14px;
                    font-weight: 500;
                    border: 1px solid #fecaca;
                }

                @media (max-width: 1024px) {
                    .car-card {
                        grid-template-columns: 1fr;
                        gap: 12px;
                    }

                    .car-meta {
                        flex-wrap: wrap;
                        gap: 12px;
                    }
                }

                @media (max-width: 768px) {
                    .my-cars-container {
                        padding: 0 16px 30px;
                    }

                    .page-header-modern {
                        flex-direction: column;
                        align-items: stretch;
                    }

                    .header-actions {
                        flex-wrap: wrap;
                    }

                    .cars-stats-grid {
                        grid-template-columns: repeat(2, 1fr);
                    }

                    .filters-modern {
                        flex-direction: column;
                        align-items: stretch;
                    }

                    .search-modern {
                        min-width: unset;
                    }

                    .filter-group {
                        justify-content: center;
                    }

                    .car-card-left {
                        flex-direction: column;
                        align-items: flex-start;
                    }

                    .car-route-modern {
                        width: 100%;
                        justify-content: space-between;
                    }

                    .car-actions {
                        justify-content: flex-end;
                    }
                }
            `}</style>

            <div className="my-cars-container">
                {/* Header */}
                <div className="page-header-modern">
                    <div>
                        <h2 style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '28px', fontWeight: 700, margin: 0 }}>Car Rentals</h2>
                        <p>Manage your luxury car reservations, rental dates, and pick-up locations.</p>
                    </div>
                    <div className="header-actions">
                        <button className="btn-refresh" onClick={() => window.location.reload()}>
                            <RefreshCw size={16} />
                            Refresh
                        </button>
                        <button className="btn-refresh">
                            <Download size={16} />
                            Export
                        </button>
                    </div>
                </div>

                {error && <div className="error-box-modern">{error}</div>}

                {/* Stats */}
                <div className="cars-stats-grid">
                    <div className="car-stat-card">
                        <div className="car-stat-icon" style={{ background: "#e8f0fe", color: "#1a73e8" }}>
                            <Car size={24} />
                        </div>
                        <div className="car-stat-content">
                            <h4>{totalRentals}</h4>
                            <p>Total Bookings</p>
                        </div>
                    </div>

                    <div className="car-stat-card">
                        <div className="car-stat-icon" style={{ background: "#fef3d6", color: "#b25900" }}>
                            <Clock size={24} />
                        </div>
                        <div className="car-stat-content">
                            <h4>{activeRentals}</h4>
                            <p>Active / Upcoming</p>
                        </div>
                    </div>

                    <div className="car-stat-card">
                        <div className="car-stat-icon" style={{ background: "#e6f4ea", color: "#1e7e34" }}>
                            <DollarSign size={24} />
                        </div>
                        <div className="car-stat-content">
                            <h4>${totalSpent.toLocaleString()}</h4>
                            <p>Total Spent</p>
                        </div>
                    </div>

                    <div className="car-stat-card">
                        <div className="car-stat-icon" style={{ background: "#f3f4f6", color: "#6b7280" }}>
                            <ShieldCheck size={24} />
                        </div>
                        <div className="car-stat-content">
                            <h4>Fully Insured</h4>
                            <p>Coverage Plan</p>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="filters-modern">
                    <div className="search-modern">
                        <Search size={18} />
                        <input
                            type="text"
                            placeholder="Search by vehicle model, location or reference..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="filter-group">
                        {["All Rentals", "Confirmed", "Pending", "Completed", "Cancelled"].map((filter) => (
                            <button
                                key={filter}
                                className={`filter-btn-modern ${activeFilter === filter ? "active" : ""}`}
                                onClick={() => setActiveFilter(filter)}
                            >
                                {filter}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Cards */}
                {loading ? (
                    <div className="loading-state-modern">
                        <Loader2 className="spinner-modern" size={36} />
                        <span style={{ color: '#6b7280' }}>Loading your car rentals...</span>
                    </div>
                ) : filteredRentals.length > 0 ? (
                    <div className="cars-grid">
                        {filteredRentals.map((rental) => {
                            const statusColor = getStatusColor(rental.status);
                            return (
                                <div key={rental.id} className="car-card">
                                    <div className="car-card-left">
                                        <div className="car-icon-box">
                                            <Car size={20} color="#294b5a" />
                                        </div>
                                        <div className="car-info">
                                            <h4>{rental.carModel}</h4>
                                            <span className="car-ref">Ref: {rental.bookingReference}</span>
                                        </div>
                                    </div>

                                    <div className="car-route-modern">
                                        <div className="route-point-car">
                                            <div className="city">{rental.pickupLocation.split(',')[0] || rental.pickupLocation}</div>
                                            <div className="code">Pickup</div>
                                        </div>
                                        <div className="route-line-car">
                                            <div className="line" />
                                        </div>
                                        <div className="route-point-car">
                                            <div className="city">{rental.returnDate ? formatDate(rental.returnDate) : 'N/A'}</div>
                                            <div className="code">Return</div>
                                        </div>
                                    </div>

                                    <div className="car-meta">
                                        <div className="meta-item-car">
                                            <span className="label">Rental Period</span>
                                            <span className="value">{formatDate(rental.pickupDate)} → {formatDate(rental.returnDate)}</span>
                                        </div>
                                        <div className="meta-item-car">
                                            <span className="label">Amount</span>
                                            <span className="value">${rental.amount.toLocaleString()}</span>
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <div 
                                            className="car-status-modern"
                                            style={{ background: statusColor.bg, color: statusColor.text }}
                                        >
                                            <span className="status-dot" style={{ background: statusColor.dot }} />
                                            {rental.status}
                                        </div>

                                        <div className="car-actions">
                                            <button 
                                                className="action-btn-modern" 
                                                title="View Details"
                                                onClick={() => handleViewDetails(rental)}
                                            >
                                                <Eye size={16} />
                                            </button>
                                            
                                            {rental.status === "Pending" || rental.status === "Confirmed" && (
                                                <button
                                                    className="action-btn-modern"
                                                    title="Cancel booking"
                                                    onClick={() => openCancelModal(rental)}
                                                >
                                                    <Ban size={16} />
                                                </button>
                                            )}

                                            {(rental.status === "Pending" || rental.status === "Cancelled") && (
                                                <button
                                                    className="action-btn-modern delete"
                                                    title="Delete"
                                                    onClick={() => openDeleteModal(rental)}
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="empty-state-modern">
                        <Car size={48} />
                        <h3>No car rentals found</h3>
                        <p>Try adjusting your search or filter criteria</p>
                    </div>
                )}
            </div>

            {/* Delete Modal */}
            {isDeleteModalOpen && rentalToDelete && (
                <div className="modal-overlay" onClick={() => !isDeleting && setIsDeleteModalOpen(false)}>
                    <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="delete-icon-wrapper">
                            <AlertTriangle size={30} />
                        </div>

                        <h3 className="delete-modal-title">Delete Booking?</h3>
                        <p className="delete-modal-desc">
                            Are you sure you want to permanently delete this car rental booking? This action cannot be undone.
                        </p>

                        <div className="delete-flight-summary">
                            <span>{rentalToDelete.carModel} ({rentalToDelete.bookingReference})</span>
                        </div>

                        {deleteError && (
                            <div className="error-box" style={{ marginBottom: "16px" }}>
                                {deleteError}
                            </div>
                        )}

                        <div className="delete-actions">
                            <button
                                className="btn-cancel"
                                onClick={() => setIsDeleteModalOpen(false)}
                                disabled={isDeleting}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn-delete-confirm"
                                onClick={confirmDelete}
                                disabled={isDeleting}
                            >
                                {isDeleting ? (
                                    <>
                                        <Loader2 className="spinner" size={16} /> Deleting...
                                    </>
                                ) : (
                                    "Delete Booking"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Cancel Modal */}
            {isCancelModalOpen && rentalToCancel && (
                <div className="modal-overlay" onClick={() => !isCancelling && setIsCancelModalOpen(false)}>
                    <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="delete-icon-wrapper">
                            <AlertTriangle size={30} />
                        </div>

                        <h3 className="delete-modal-title">Cancel this booking?</h3>
                        <p className="delete-modal-desc">
                            This will cancel your car rental request. You can always book again later.
                        </p>

                        <div className="delete-flight-summary">
                            <span>{rentalToCancel.carModel} ({rentalToCancel.bookingReference})</span>
                        </div>

                        {cancelError && (
                            <div className="error-box" style={{ marginBottom: "16px" }}>
                                {cancelError}
                            </div>
                        )}

                        <div className="delete-actions">
                            <button
                                className="btn-cancel"
                                onClick={() => setIsCancelModalOpen(false)}
                                disabled={isCancelling}
                            >
                                Keep Booking
                            </button>
                            <button
                                className="btn-delete-confirm"
                                onClick={confirmCancel}
                                disabled={isCancelling}
                            >
                                {isCancelling ? (
                                    <>
                                        <Loader2 className="spinner" size={16} /> Cancelling...
                                    </>
                                ) : (
                                    "Cancel Booking"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Details Modal */}
            {isDetailsModalOpen && selectedRental && (
                <div className="modal-overlay" onClick={() => setIsDetailsModalOpen(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3><Car size={20} /> Rental Details</h3>
                            <button className="modal-close-btn" onClick={() => setIsDetailsModalOpen(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="modal-section">
                                <div className="modal-section-title"><Car size={14} /> Vehicle Information</div>
                                <div className="details-grid">
                                    <div className="detail-item">
                                        <span className="detail-label">Model</span>
                                        <span className="detail-value">{selectedRental.carModel}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">Type</span>
                                        <span className="detail-value">{selectedRental.carType}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">Reference</span>
                                        <span className="detail-value">{selectedRental.bookingReference}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">Status</span>
                                        <span className={`badge-status badge-${selectedRental.status.toLowerCase()}`}>
                                            {selectedRental.status}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-section">
                                <div className="modal-section-title"><Calendar size={14} /> Rental Period</div>
                                <div className="details-grid">
                                    <div className="detail-item">
                                        <span className="detail-label">Pickup Date</span>
                                        <span className="detail-value">{formatDate(selectedRental.pickupDate)}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">Return Date</span>
                                        <span className="detail-value">{formatDate(selectedRental.returnDate)}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">Location</span>
                                        <span className="detail-value">{selectedRental.pickupLocation}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">Amount</span>
                                        <span className="detail-value">${selectedRental.amount.toLocaleString()}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </CustomerLayout>
    );
};

export default MyRentCars;